/**
 * Footer Component
 *
 * Site footer with legal links, copyright, and company information.
 */

import { memo } from 'react';
import { ENV } from '@/config/env';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterProps {
  className?: string;
  showInSidebar?: boolean;
}

const websiteBase = ENV.WEBSITE_BASE_URL.replace(/\/$/, '');

const LEGAL_LINKS: FooterLink[] = [
  { label: 'Privacy Policy', href: `${websiteBase}/privacy-policy` },
  { label: 'Terms of Service', href: `${websiteBase}/terms-of-service` },
  { label: 'Cookie Policy', href: `${websiteBase}/cookie-policy` },
];

/** Compact footer labels used by the main-content footer — matches the
 *  Privacy / Terms / Support trio in DemoChromeA.tsx. Destinations preserve
 *  the real-app URLs from LEGAL_LINKS plus the public support page. */
const COMPACT_FOOTER_LINKS: FooterLink[] = [
  { label: 'Privacy', href: `${websiteBase}/privacy-policy` },
  { label: 'Terms', href: `${websiteBase}/terms-of-service` },
  { label: 'Support', href: `${websiteBase}/support` },
];

export const Footer: React.FC<FooterProps> = ({ className = '', showInSidebar = false }) => {
  const currentYear = new Date().getFullYear();

  // Compact footer for sidebar
  if (showInSidebar) {
    return (
      <footer className={`px-4 py-3 border-t border-gray-200 ${className}`}>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
          {LEGAL_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-700 transition-colors"
            >
              {link.label}
            </a>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">&copy; {currentYear} DomainBoost</p>
      </footer>
    );
  }

  // Full footer for main content area — matches DemoChromeA.tsx footer:
  // thin white bar pinned below the content with copyright on the left and
  // Privacy/Terms/Support links on the right.
  return (
    <footer className={`border-t border-border bg-white px-[24px] py-[16px] ${className}`}>
      <div className="flex flex-col items-center justify-between gap-[8px] text-[12px]/[16px] text-muted-foreground sm:flex-row">
        <p className="text-[12px]/[16px] font-normal">
          &copy; {currentYear} DomainBoost. All rights reserved.
        </p>
        <nav className="flex items-center gap-[16px]" aria-label="Footer">
          {COMPACT_FOOTER_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors duration-300 hover:text-foreground"
            >
              {link.label}
            </a>
          ))}
        </nav>
      </div>
    </footer>
  );
};

/**
 * Minimal footer for auth pages (login, register, etc.)
 */
export const MinimalFooter: React.FC<{ className?: string }> = ({ className = '' }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className={`py-4 ${className}`}>
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm text-gray-500">
        {LEGAL_LINKS.map((link, index) => (
          <span key={link.href} className="flex items-center">
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-700 transition-colors"
            >
              {link.label}
            </a>
            {index < LEGAL_LINKS.length - 1 && <span className="ml-4 text-gray-300">|</span>}
          </span>
        ))}
      </div>
      <p className="text-center text-xs text-gray-400 mt-2">
        &copy; {currentYear} DomainBoost. All rights reserved.
      </p>
    </footer>
  );
};

export default memo(Footer);
